import { apiClient } from './api';

export interface Tag {
  tag_id: number;
  tag_name: string;
}

export interface TagWithUsage extends Tag {
  usage_count?: number;
}

export interface CreateTagRequest {
  tag_name: string;
}

export interface UpdateTagRequest {
  tag_name: string;
}

export interface TagStats {
  total_tags: number;
  most_used: TagWithUsage[];
  least_used: TagWithUsage[];
  recent_tags: Tag[];
  unused_tags: Tag[];
}

export class TagService {
  async getTags(params: {
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: 'tag_name' | 'usage_count';
    sortDirection?: 'asc' | 'desc';
    includeUsageCount?: boolean;
  } = {}, admin = false): Promise<{
    tags: TagWithUsage[];
    total: number;
    offset: number;
    limit: number;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params.search) searchParams.append('search', params.search);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortDirection) searchParams.append('sortDirection', params.sortDirection);
    if (params.includeUsageCount) searchParams.append('includeUsageCount', 'true');

    const response = await apiClient.get<{
      message: string;
      data: TagWithUsage[];
      pagination: {
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
      };
    }>(`${admin ? '/tags/admin' : '/tags'}?${searchParams}`);
    
    return {
      tags: response.data,
      total: response.pagination.total,
      offset: response.pagination.offset,
      limit: response.pagination.limit,
    };
  }

  async getAdminTags(params: Parameters<TagService['getTags']>[0] = {}): ReturnType<TagService['getTags']> {
    return this.getTags(params, true);
  }

  async getTag(id: number): Promise<TagWithUsage> {
    const response = await apiClient.get<{
      message: string;
      data: TagWithUsage;
    }>(`/tags/${id}?includeUsageCount=true`);
    
    return response.data;
  }

  async getAdminTag(id: number): Promise<TagWithUsage> {
    const response = await apiClient.get<{
      message: string;
      data: TagWithUsage;
    }>(`/tags/admin/${id}?includeUsageCount=true`);
    return response.data;
  }

  async createTag(tag: CreateTagRequest): Promise<Tag> {
    const response = await apiClient.post<{
      message: string;
      data: Tag;
    }>('/tags/admin', tag);
    
    return response.data;
  }

  async updateTag(id: number, updates: UpdateTagRequest): Promise<Tag> {
    const response = await apiClient.put<{
      message: string;
      data: Tag;
    }>(`/tags/admin/${id}`, updates);
    
    return response.data;
  }

  async deleteTag(id: number): Promise<void> {
    await apiClient.delete(`/tags/admin/${id}`);
  }

  async getArtworksForTag(tagId: number, limit?: number, offset?: number, admin = false): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (limit) searchParams.append('limit', limit.toString());
    if (offset) searchParams.append('offset', offset.toString());

    const response = await apiClient.get<{
      message: string;
      data: any[];
    }>(`${admin ? `/tags/admin/${tagId}/artworks` : `/tags/${tagId}/artworks`}?${searchParams}`);
    
    return response.data;
  }

  async getAdminArtworksForTag(tagId: number, limit?: number, offset?: number): Promise<any[]> {
    return this.getArtworksForTag(tagId, limit, offset, true);
  }

  async setArtworkTags(artworkId: number, tagIds: number[]): Promise<void> {
    await apiClient.put(`/tags/artworks/${artworkId}/tags/admin`, { tagIds });
  }

  // Search and filtering
  async searchTags(query: string): Promise<Tag[]> {
    const params = new URLSearchParams({ search: query });
    return apiClient.get<Tag[]>(`/tags/search?${params}`);
  }

  async getPopularTags(limit = 10): Promise<TagWithUsage[]> {
    // Use the regular tags endpoint with includeUsageCount and sorting
    const params = new URLSearchParams({ 
      limit: limit.toString(),
      includeUsageCount: 'true',
      sortBy: 'usage_count',
      sortDirection: 'desc'
    });
    
    // Backend returns data in different format, so we need to adapt
    const response = await apiClient.get<{
      message: string;
      data: TagWithUsage[];
      pagination: {
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
      };
    }>(`/tags?${params}`);
    
    return response.data;
  }

  async getRecentTags(limit = 10): Promise<Tag[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    return apiClient.get<Tag[]>(`/tags/recent?${params}`);
  }

  async getUnusedTags(): Promise<Tag[]> {
    return apiClient.get<Tag[]>('/tags/unused');
  }

  // Tag statistics
  async getTagStats(): Promise<TagStats> {
    return apiClient.get<TagStats>('/tags/stats');
  }

  async getTagUsageHistory(id: number, days = 30): Promise<Array<{
    date: string;
    usage_count: number;
    artworks: number;
    exhibitions: number;
  }>> {
    const params = new URLSearchParams({ days: days.toString() });
    return apiClient.get(`/tags/${id}/usage-history?${params}`);
  }

  // Tag relationships
  async getRelatedTags(id: number, limit = 10): Promise<Array<{
    tag: Tag;
    correlation_score: number;
    shared_artworks: number;
  }>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    return apiClient.get(`/tags/${id}/related?${params}`);
  }

  async getTagCombinations(limit = 20): Promise<Array<{
    tags: string[];
    usage_count: number;
    recent_usage: string;
  }>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    return apiClient.get(`/tags/combinations?${params}`);
  }

  // Bulk operations
  async createMultipleTags(names: string[]): Promise<{
    created: Tag[];
    duplicates: string[];
    errors: Array<{ name: string; error: string }>;
  }> {
    return apiClient.post('/tags/bulk-create', { names });
  }

  async bulkDeleteTags(ids: number[]): Promise<{
    success: number;
    failed: number;
    errors: Array<{ id: number; error: string }>;
  }> {
    const results = await Promise.allSettled(
      ids.map(id => this.deleteTag(id))
    );

    let successCount = 0;
    const errors: Array<{ id: number; error: string }> = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successCount++;
      } else {
        errors.push({
          id: ids[index],
          error: result.reason?.message || 'Unknown error occurred',
        });
      }
    });

    return {
      success: successCount,
      failed: errors.length,
      errors,
    };
  }

  async mergeTags(sourceIds: number[], targetId: number): Promise<{
    success: boolean;
    merged_count: number;
    target_tag: Tag;
    error?: string;
  }> {
    return apiClient.post(`/tags/${targetId}/merge`, { source_ids: sourceIds });
  }

  // Tag cleanup operations
  async cleanupUnusedTags(): Promise<{
    deleted_count: number;
    deleted_tags: string[];
  }> {
    return apiClient.delete('/tags/cleanup-unused');
  }

  async renameSimilarTags(threshold = 0.8): Promise<{
    suggestions: Array<{
      group: string[];
      suggested_name: string;
      confidence: number;
    }>;
  }> {
    const params = new URLSearchParams({ threshold: threshold.toString() });
    return apiClient.get(`/tags/similarity-suggestions?${params}`);
  }

  // Validation helpers
  validateTagName(name: string): { valid: boolean; error?: string } {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return { valid: false, error: 'Tag name is required' };
    }

    if (trimmedName.length < 2) {
      return { valid: false, error: 'Tag name must be at least 2 characters' };
    }

    if (trimmedName.length > 50) {
      return { valid: false, error: 'Tag name must be less than 50 characters' };
    }

    if (!/^[a-zA-Z0-9\s-_]+$/.test(trimmedName)) {
      return { valid: false, error: 'Tag name can only contain letters, numbers, spaces, hyphens, and underscores' };
    }

    return { valid: true };
  }

  validateTagNames(names: string[]): {
    valid: string[];
    invalid: Array<{ name: string; error: string }>;
  } {
    const valid: string[] = [];
    const invalid: Array<{ name: string; error: string }> = [];

    names.forEach((name) => {
      const validation = this.validateTagName(name);
      if (validation.valid) {
        valid.push(name.trim());
      } else {
        invalid.push({ name, error: validation.error! });
      }
    });

    return { valid, invalid };
  }

  // Utility methods
  normalizeTagName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^a-z0-9\s-_]/g, '');
  }

  formatTagForDisplay(tag: Tag | string): string {
    const name = typeof tag === 'string' ? tag : tag.tag_name;
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  groupTagsByCategory(tags: Tag[]): Record<string, Tag[]> {
    const groups: Record<string, Tag[]> = {};
    
    tags.forEach(tag => {
      const category = this.inferTagCategory(tag.tag_name);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tag);
    });

    return groups;
  }

  private inferTagCategory(tagName: string): string {
    const name = tagName.toLowerCase();
    
    // Color-related tags
    if (/\b(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|color|colored?)\b/.test(name)) {
      return 'Colors';
    }
    
    // Medium-related tags
    if (/\b(paint|painting|pencil|marker|crayon|watercolor|acrylic|oil|digital|collage|clay|sculpture)\b/.test(name)) {
      return 'Medium';
    }
    
    // Subject-related tags
    if (/\b(portrait|landscape|still.?life|abstract|figure|animal|nature|flower|tree|house|car)\b/.test(name)) {
      return 'Subject';
    }
    
    // Grade/Age-related tags
    if (/\b(grade|kindergarten|elementary|middle|high|age|year|old|k-\d|\d+th)\b/.test(name)) {
      return 'Grade/Age';
    }
    
    // Event/Project-related tags
    if (/\b(project|assignment|exhibition|contest|show|event|class|workshop)\b/.test(name)) {
      return 'Projects/Events';
    }
    
    return 'Other';
  }

  generateTagSuggestions(inputText: string, existingTags: Tag[]): string[] {
    const suggestions: string[] = [];
    const words = inputText.toLowerCase().split(/\s+/);
    
    // Find similar existing tags
    existingTags.forEach(tag => {
      const tagWords = tag.tag_name.toLowerCase().split(/\s+/);
      const hasCommonWord = words.some(word => 
        tagWords.some((tagWord: string) => 
          tagWord.includes(word) || word.includes(tagWord)
        )
      );
      if (hasCommonWord && !suggestions.includes(tag.tag_name)) {
        suggestions.push(tag.tag_name);
      }
    });

    return suggestions.slice(0, 5); // Return top 5 suggestions
  }

  // Tag hierarchy helpers (for future use)
  async getTagHierarchy(): Promise<Array<{
    parent: Tag | null;
    children: Tag[];
  }>> {
    return apiClient.get('/tags/hierarchy');
  }

  async setTagParent(childId: number, parentId: number | null): Promise<Tag> {
    return apiClient.patch(`/tags/${childId}/parent`, { parent_id: parentId });
  }
}

export const tagService = new TagService();
export default tagService;
